/**
 * On-box app API client.
 *
 * Base: https://fs.elefa.tel/app-api
 * Auth: Authorization: Bearer <supabase access_token>. The box verifies via
 *       Supabase /auth/v1/user — no shared secret in the client.
 *
 * All money values are AUD-native (server-side). Callers MUST NOT apply any
 * client-side FX conversion (no ×1.55, no useFxRates) on the returned figures.
 *
 * This is the single source of truth for the two display surfaces named in
 * the Option A rewire spec: wallet balance + per-number dialer rate.
 */
import { supabase } from "@/integrations/supabase/client";

export const APP_API_BASE = "https://fs.elefa.tel/app-api";

export interface AppApiBalance {
  account: string;
  balance: number; // AUD, decimal
  currency: string; // always "AUD"
  provisioned: boolean;
}

export interface AppApiRate {
  number: string;
  rate_per_min: number; // AUD, decimal
  currency: string; // always "AUD"
  destination: string;
  available: boolean;
}

class AppApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "AppApiError";
  }
}

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Low-level GET. Fails loud — no silent fallback to legacy paths. Silent
 * fallback is what produced the FX drift we're fixing.
 */
async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new AppApiError(401, "AUTH_REQUIRED");

  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), 5000);
  const merged = signal
    ? new AbortController()
    : ctrl;
  if (signal) {
    signal.addEventListener("abort", () => merged.abort(), { once: true });
    ctrl.signal.addEventListener("abort", () => merged.abort(), { once: true });
  }

  try {
    const res = await fetch(`${APP_API_BASE}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal: merged.signal,
    });
    if (!res.ok) {
      throw new AppApiError(res.status, `HTTP ${res.status} on ${path}`);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchAppApiBalance(signal?: AbortSignal): Promise<AppApiBalance> {
  return apiGet<AppApiBalance>("/balance", signal);
}

/**
 * @param e164Digits E.164 digits without the leading "+", e.g. "21192555000".
 *                   The box normalises further but we send digits-only for cache stability.
 */
export async function fetchAppApiRate(
  e164Digits: string,
  signal?: AbortSignal,
): Promise<AppApiRate> {
  const q = encodeURIComponent(e164Digits);
  return apiGet<AppApiRate>(`/rate?number=${q}`, signal);
}

export { AppApiError };
